// AidWaive Net Price Calculator — bracket mapping + asset adjustment.
//
// As of v4.1 we ask Q2 (income) and Q4 (savings) as exact currency
// values via `CurrencyInput`, instead of radio buckets. The federal
// Scorecard data still only ships net-price figures by 5 income brackets,
// so we map any exact dollar amount onto one of those buckets here.
//
// Q3 (home equity) and Q5 (retirement) remain coarse radio options
// because (a) home equity is hard to know exactly and (b) retirement
// is excluded from the FAFSA SAI calculation entirely.

export const INCOME_BRACKETS = [
  { label: "Under $30,000",          apiKey: "under_30k", federalLabel: "Under $30,000",   short: "Under $30K" },
  { label: "$30,000 to $48,000",     apiKey: "30k_48k",   federalLabel: "$30K to $48K",    short: "$30K-$48K" },
  { label: "$48,000 to $75,000",     apiKey: "48k_75k",   federalLabel: "$48K to $75K",    short: "$48K-$75K" },
  { label: "$75,000 to $110,000",    apiKey: "75k_110k",  federalLabel: "$75K to $110K",   short: "$75K-$110K" },
  { label: "$110,000 to $150,000",   apiKey: "over_110k", federalLabel: "$110K and up",    short: "$110K-$150K" },
  { label: "Over $150,000",          apiKey: "over_110k", federalLabel: "$110K and up",    short: "Over $150K" },
];

export const HOME_EQUITY_OPTIONS = [
  { value: "rent",         label: "We rent or do not own a home" },
  { value: "under_100k",   label: "Under $100,000" },
  { value: "100k_300k",    label: "$100,000 to $300,000" },
  { value: "300k_500k",    label: "$300,000 to $500,000" },
  { value: "over_500k",    label: "Over $500,000" },
];

export const RETIREMENT_OPTIONS = [
  { value: "under_50k",    label: "Under $50,000" },
  { value: "50k_200k",     label: "$50,000 to $200,000" },
  { value: "over_200k",    label: "Over $200,000" },
  { value: "unknown",      label: "Not sure or prefer not to say" },
];

/**
 * Map an exact income (number or numeric string) to one of the 6
 * user-facing brackets. Values at the edges fall into the lower bracket
 * (e.g. exactly $48,000 -> "$30,000 to $48,000").
 */
export function getBracketKeyFromIncome(income) {
  const n = Number(income) || 0;
  if (n < 30000)  return INCOME_BRACKETS[0];
  if (n < 48000)  return INCOME_BRACKETS[1];
  if (n < 75000)  return INCOME_BRACKETS[2];
  if (n < 110000) return INCOME_BRACKETS[3];
  if (n < 150000) return INCOME_BRACKETS[4];
  return INCOME_BRACKETS[5];
}

/**
 * Asset adjustment, in dollars per year added on top of the federal
 * bracket. Schools that use the CSS Profile factor home equity and
 * non-retirement assets into their aid formula. We model this as a
 * small upward adjustment so the estimate is shown as a range, not a
 * false-precise single number.
 *
 * @param {string} homeEquity   one of HOME_EQUITY_OPTIONS.value
 * @param {string|number} savingsAmount   exact savings dollars
 */
export function assetAdjustment(homeEquity, savingsAmount) {
  let adj = 0;
  if (homeEquity === "300k_500k") adj += 1200;
  if (homeEquity === "over_500k") adj += 2800;

  const s = Number(savingsAmount) || 0;
  if (s >= 150000)      adj += 3500;
  else if (s >= 50000)  adj += 1500;
  else if (s >= 10000)  adj += 500;
  return adj;
}

/**
 * Format the estimate as a range. If adjustment is zero, returns a single
 * value. Otherwise returns "$14,200 to $17,000".
 */
export function formatRange(base, adjustment) {
  if (!base) return null;
  const lo = Math.round(base);
  const hi = Math.round(base + adjustment);
  if (adjustment <= 0) return { lo, hi: lo, display: `$${lo.toLocaleString()}` };
  return { lo, hi, display: `$${lo.toLocaleString()} to $${hi.toLocaleString()}` };
}

/**
 * Pretty-print an exact income for the result headline:
 *   85000  -> "$85,000"
 */
export function formatIncomeExact(income) {
  const n = Number(income) || 0;
  return `$${n.toLocaleString()}`;
}

// Legacy helper kept for any callers still passing a label string.
// Prefer `getBracketKeyFromIncome(number)` going forward.
export function bracketForIncomeLabel(label) {
  return INCOME_BRACKETS.find(b => b.label === label) || INCOME_BRACKETS[0];
}
